import { Link } from "react-router-dom";
import PageHeader from "~/shared/components/page-header.component";
import useAuthStore from "~/store/auth.store";
import useSidebarStore from "~/store/sidebar.store";
import { UserRole } from "~/shared/config";
import { Text, Box, Card, Separator, Flex, Tooltip } from "@radix-ui/themes";
import { CheckCircle, ChevronRight, CircleCheck, LogOutIcon } from "lucide-react";
import { networkService } from "~/services";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import useOnboardStore from "~/store/onboard.store";
import CustomModal from "~/shared/components/customModal";
import { handleError } from "~/shared/utils/errors.util";
import { IError } from "~/interfaces/shared.interface";
import { PROFILE_INFO_API } from "~/shared/constants/api";
import { formatDistanceToNow } from "date-fns";

export default function Profile() {
  const { user } = useAuthStore((state) => ({
    user: state.user,
  }));
  const [collapsed] = useSidebarStore((state) => [state.collapsed]);

  const [isRequestSent, setIsRequestSent] = useState(false); // State to track if the request has been sent

  const { deviceId } = useOnboardStore((state) => state);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Check if deletion request has been made using the user details
  const isDeletionRequested = userDetails?.deletionRequestedAt !== null &&
    userDetails?.deletionRequestedAt !== undefined;

  const getOrganizationDetails = async (userId: string) => {
    try {
      const response = await networkService.get<any>(
        `${PROFILE_INFO_API}/${userId}`
      );
      return response;
    } catch (error) {
      const err = error as IError;
      handleError({ error: err, message: err.message, level: "error" });
      err?.message && toast.error(err.message);
      return null;
    }
  };

  // Fetch user details on component mount
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user?.userId) {
        setLoading(true);
        const response = await getOrganizationDetails(user.userId);
        if (response && response.data) {
          setUserDetails(response.data);
        }
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [user?.userId]);

  const formatNameInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`;
    }
    return "T";
  }

  const handleDeleteRequest = async () => {
    try {
      await networkService.post(`/users/${user.userId}/request-deletion`, {
        deviceId: deviceId
      });
      setIsDeleteModalOpen(false);
      toast.success("Account deletion request sent successfully");

      // Refresh user details to get updated deletionRequestedAt
      const response = await getOrganizationDetails(user.userId);
      if (response && response.data) {
        setUserDetails(response.data);
      }
    } catch (error) {
      toast.error("Failed to send account deletion request");
      console.error("Error sending deletion request:", error);
    }
  };

  const getTimeSinceRequest = () => {
    if (!userDetails?.deletionRequestedAt) return "";

    try {
      const requestDate = new Date(userDetails.deletionRequestedAt);
      return `Request sent ${formatDistanceToNow(requestDate, { addSuffix: true })}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Request sent";
    }
  };

  return (
    <div
      className={`flex flex-col flex-grow lg:px-12 lg:py-4 p-8 overflow-hidden ${collapsed ? `${user?.role === UserRole.Admin ? "ml-20" : "md:ml-20"}` : `${user?.role === UserRole.Admin ? "ml-72" : "md:ml-72"}`} transition-all duration-300 pb-24`}>
      <h1 className="font-space-grotesk text-[34px] font-bold mt-6">Profile</h1>
      <Box>
        <Card size="1" className="font-space-grotesk p-6 shadow-md mt-7">
          <Flex gap="20px" align="center" className="w-full">
            <Text className="bg-[#EDEDFA] rounded-full text-[#4C4097] uppercase text-lg font-bold min-w-[53px] h-[53px] flex justify-center items-center">
              {formatNameInitials()}
            </Text>
            <Box className="flex flex-col min-w-0">
              <Text as="div" weight="bold" className="capitalize text-[20px] truncate max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {user?.firstName ? `${user?.firstName} ${user?.lastName}` : "N/A"}
              </Text>
              <Text as="div" className="text-[18px] truncate max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {user?.email || "N/A"}
              </Text>
            </Box>
          </Flex>
          <Separator orientation="horizontal" size="4" className="mt-5" />
          <Link to="/profile/update-user">
            <Flex justify="between" align="center" className="mt-5">
              <Text className="text-[19px]">
                Edit profile
              </Text>
              <ChevronRight />
            </Flex>
          </Link>
          <Separator orientation="horizontal" size="4" className="mt-5" />
          <Link to="/profile/reset-password">
            <Flex justify="between" align="center" className="mt-5">
              <Text className="text-[19px]">
                Change password
              </Text>
              <ChevronRight />
            </Flex>
          </Link>
          {/* <Separator orientation="horizontal" size="4" className="mt-5" />
          <div onClick={() => setIsDeleteModalOpen(true)} className="cursor-pointer">
            <Flex justify="between" align="center" className="mt-5">
              <Text className="text-[19px] text-red-500">
                Delete my account
              </Text>
              <ChevronRight />
            </Flex>
          </div>
          {isRequestSent && (
            <Flex align="center" gap="1" className="bg-gray-100 px-3 py-1 rounded-full">
              <CheckCircle size={16} className="text-gray-600" />
              <Text className="text-gray-600">Request sent</Text>
            </Flex>
          )} */}
          <Separator orientation="horizontal" size="4" className="mt-5" />
          <div onClick={() => !isDeletionRequested && setIsDeleteModalOpen(true)} className={`${!isDeletionRequested ? 'cursor-pointer' : ''}`}>
            <Flex justify="between" align="center" className="mt-5">
              <Flex align="center" gap="4">
                <Text className="text-[19px] text-red-500">
                  Delete my account
                </Text>
                {isDeletionRequested && (
                  <Tooltip content={getTimeSinceRequest()}>
                    <Flex align="center" gap="1" className="bg-[#F2EFEE] px-3 py-1 rounded-sm cursor-default">
                      <CircleCheck size={16} className="text-gray-600" />
                      <Text className="text-[#544B48] text-sm">Request sent</Text>
                    </Flex>
                  </Tooltip>
                )}
              </Flex>
              <ChevronRight />
            </Flex>
          </div>
        </Card>
      </Box>

      <div className="mt-7 flex md:flex-row flex-col flex-wrap gap-4">
        <div className="flex justify-center items-center lg:w-44 bg-angular-gradient rounded-full p-0.5">
          <Link
            to={`/logout`}
            className="flex justify-center items-center gap-2 bg-rounded-full text-[18px] font-semibold sm:text-base md:text-base font-space-grotesk w-full bg-white-a700 rounded-full p-2 hover:bg-light-angular-gradient">
            <LogOutIcon size={19} />
            Log out
          </Link>
        </div>
      </div>

      {/* Account Deletion Modal */}
      {/* <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        showCloseButton={false}
        width="max-w-[600px]"
        rounded="3xl" // Using the new rounded prop
      >
        <div className="font-space-grotesk">
          <div className="flex justify-between items-center p-2">
            <span className="font-medium">Account deletion</span>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="text-2xl"
            >
              ×
            </button>
          </div>

          <div className="px-3 py-3">
            <h2 className="text-3xl font-bold mb-5">Sure you want to delete your account?</h2>

            <div className="mb-3"></div>

            <p className="text-base mb-16">
              A request will be sent to our support team and processed as soon as
              possible. They will contact you directly by e-mail to inform you of the
              effective deletion of your account.
            </p>

            <div className="flex flex-col gap-4">
              <button
                onClick={handleDeleteRequest}
                className="w-full py-1 rounded-full text-red-500 border-[2px] border-red-500 bg-[#FEF2F2] font-medium"
              >
                Send my deletion request
              </button>

              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full py-1 rounded-full font-medium border-[2px] border-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </CustomModal> */}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black-900 bg-opacity-50">
          <div className="bg-white-a700 shadow-lg relative mb-12 max-w-[600px] rounded-3xl mx-5">
            <div className="font-space-grotesk">
              {/* Header with close button */}
              <div className="flex justify-between items-center px-7 pb-4 pt-6">
                <span className="font-medium">Account deletion</span>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Horizontal separator that spans full width */}
              <div className="w-full h-px bg-gray-200"></div>

              <div className="px-7 py-5">
                <h2 className="text-3xl font-bold mb-5">Sure you want to delete your account?</h2>

                <p className="text-base mb-16">
                  A request will be sent to our support team and processed as soon as
                  possible. They will contact you directly by e-mail to inform you of the
                  effective deletion of your account.
                </p>

                <div className="flex flex-col gap-4 mb-3">
                  <button
                    onClick={handleDeleteRequest}
                    className="w-full py-1 rounded-full text-red-500 border-[2px] border-red-500 bg-[#FEF2F2] font-medium"
                  >
                    Send my deletion request
                  </button>

                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="w-full py-1 rounded-full font-medium border-[2px] border-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}